import {ProductSliderCardProps} from '@local/ui/blocks/productSlider/ProductSliderCard'
import {Button, Locale, Pastil, SanityImage, SmallText} from '@local/ui/components'
import {cx} from '@local/utils/cx'
import {ForwardedRef, forwardRef} from 'react'

export const ProductSliderListItem = forwardRef(
	(
		{
			id,
			tag,
			slug,
			image,
			index,
			title,
			item,
			disabled,
			className,
			locationLabel,
			disabledMessageList,
			notifyClick,
		}: ProductSliderCardProps,
		ref: ForwardedRef<HTMLDivElement>,
	) => {
		const isComingSoon = tag === 'coming-soon'
		return (
			<article
				id={id}
				ref={ref}
				className={cx(
					'w-full relative h-fit group',
					className)}>
				<Button
					disabled={disabled}
					href={slug}
					className={cx(
						'flex flex-row items-center gutter-gap-1 relative w-full text-left group/btn',
						'py-20 border-b border-grey-1/30 outline-none',
						'after:absolute after:-bottom-1 overflow-y-visible after:left-0',
						'after:w-full after:h-1 after:bg-grey-1/0',
						'group-hover:after:bg-grey-1 focus-visible:after:bg-grey-1 focus-within:after:bg-grey-1',
						'after:transition-color after:duration-500 after:delay-150 after:ease-quart-out after:transform-gpu',
						index === 0 && 'border-t',
						disabled && 'hidden',
					)}>
					<figure className='span-w-2 h-64 lg:span-w-1 lg:h-120 object-cover overflow-clip'>
						<SanityImage
							image={image}
							alt={title}
							width={98}
							height={120}
							desktopWidth={98}
							priority={index < 4}
							className={cx(
								'span-w-1 h-120 object-cover',
								'scale-110 group-hover:scale-100 transition-transform duration-500 ease-[cubic-bezier(.35,0,.19,1)]',
								'group-focus-visible/btn:scale-100',
							)}
							desktopOnly />
						<SanityImage
							image={image}
							alt={title}
							width={51}
							height={64}
							mobileWidth={51}
							priority={index < 4}
							className='span-w-2 h-64 object-cover'
							mobileOnly />
					</figure>
					<div className={cx(
						'text-grey-5 flex-1',
						'lg:flex lg:w-full lg:gutter-gap-x-1 items-center',
					)}>
						<div>
							{tag === 'kaer-choice' && <Pastil variant='filled' className='mb-3 lg:mb-9'><Locale>Kaer Choice</Locale></Pastil>}
							<h3 className={cx(
								'font-serif capitalize text-16 tracking-tight lg:text-20 leading-snug lg-max:mb-16 whitespace-normal',
								'lg:span-w-3 lg:row-span-2 lg:col-start-1',
							)}>
								{item.title}
							</h3>
						</div>
						<div>
							<SmallText
								textColor='black' className={cx(
									'lg:text-12 font-medium leading-none lg:span-w-2 lg:row-start-1 lg:col-start-2',
								)}>
								{item.slugPrefix === 'experience' && (
									<>
										<span>{locationLabel}: </span><span>{item.location}</span>
									</>
								)}
								{item.slugPrefix === 'merch' && (
									<span>{item.productType}</span>
								)}
							</SmallText>
							{item.slugPrefix === 'experience' && tag === 'coming-soon'
								? <Pastil className='mt-5 lg:row-start-2 lg:col-start-2' variant='grey'><Locale>Coming Soon</Locale></Pastil>
								: (
									<SmallText
										textColor='black' className={cx(
											'lg:text-12 font-medium leading-none lg:span-w-2 lg:row-start-2 lg:col-start-2',
										)}>
										{item.datePeriod}
									</SmallText>
								)}
						</div>
					</div>
					<Button
						type='button'
						as='div'
						variant='underlined'
						tabIndex={isComingSoon ? 0 : undefined}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								e.preventDefault()
								e.stopPropagation()
								notifyClick?.()
							}
						}}
						className={cx(
							'lg-max:mt-auto mb-5 text-12 uppercase font-medium pb-4',
							'after:scale-x-100 hover:after:scale-x-0 hover:after:origin-right',
							'focus-visible:after:scale-x-0 focus-visible:after:origin-right',
						)}>
						{tag === 'coming-soon' ? <Locale>Notify Me</Locale> : <Locale>Discover</Locale>}
					</Button>
				</Button>
				<div className={cx(
					'py-20 border-b border-grey-1/30',
					index === 0 && 'border-t',
					!disabled && 'hidden',
				)}>
					<div className='bg-grey-5/5 h-64 lg:h-120 grid place-content-center text-center'>
						{disabledMessageList && (
							<h3 className='font-serif text-14 lg:text-24 leading-snug text-grey-4 lg:span-w-5'>
								{disabledMessageList}
							</h3>
						)}
					</div>

				</div>
			</article>
		)
	},
)
